import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { getSlideSets, SlideSet } from '../../../../api/slide-set';
import IdentityContext from '../../../../contexts/IdentityContext';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import ModuleContext from '../../_contexts/ModuleContext';
import TableOfContentsContext from './TableOfContentsContext';

type SlideSetsContextValue = {
  slideSets: SlideSet[];
  setSlideSets: React.Dispatch<React.SetStateAction<SlideSet[]>>;
  fetchSlideSets: () => void;
  isLoading: boolean;
};

const SlideSetsContext = createContext<SlideSetsContextValue>({
  slideSets: [],
  setSlideSets: () => undefined,
  fetchSlideSets: () => undefined,
  isLoading: true,
});

export default SlideSetsContext;

export function SlideSetsContextProvider({ children }: PropsWithChildren) {
  const handleError = useThrowAsyncError();

  const { identity } = useContext(IdentityContext);
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);

  const [slideSets, setSlideSets] = useState<SlideSet[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSlideSets = useCallback(async () => {
    if (!selectedModule || !selectedNode) return;
    setIsLoading(true);
    try {
      const data = await getSlideSets(selectedModule.id, selectedNode.id);

      setSlideSets(data);
    } catch (e) {
      handleError(e);
    } finally {
      setIsLoading(false);
    }
  }, [handleError, selectedModule, selectedNode]);

  useEffect(() => {
    if (identity.isTeacherAlike) fetchSlideSets();
    else setIsLoading(false);
  }, [fetchSlideSets, identity.isTeacherAlike]);

  return (
    <SlideSetsContext.Provider value={{ slideSets, setSlideSets, fetchSlideSets, isLoading }}>
      {children}
    </SlideSetsContext.Provider>
  );
}
